import React, { useState, useEffect } from "react";
import { Container, Button } from "react-bootstrap";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const services = [
  { id: "ai", label: "Conseil Stratégique", color: "#5b7dfd", gradient: "from-blue-400 to-cyan-400", path: "/bu-msit-advisory" },
  { id: "service", label: "Conseil Technologique", color: "#a855f7", gradient: "from-purple-400 to-pink-400", path: "/bu-msit-conseil" },
  { id: "je", label: "Pôle Intelligence Artificielle", color: "#f43f5e", gradient: "from-rose-400 to-orange-400", path: "/bu-msit-ai" },
  { id: "jk", label: "Centre de Services & Delivery", color: "#10b981", gradient: "from-emerald-400 to-teal-400", path: "/bu-msit-services" }
];

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % services.length);
    }, 2500); // Cycle every 2.5 seconds
    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <section className="hero-section">
      <div className="text-center">
        <p className="subtitle">{t("hero.subtitle")}</p>
        <h1 className="title">
          {t("hero.title")} <br /> {t("hero.title1")}
        </h1>
        
        <div className="services-container">
          <div className="services-wrapper">
            {services.map((service, index) => {
              const isActive = index === activeIndex;
              const isHovered = hoveredIndex === index;
              const shouldShowGlow = isActive || isHovered;
              
              // Map gradient classes to actual gradient values
              const gradientMap = {
                "from-blue-400 to-cyan-400": "linear-gradient(to right, #60a5fa, #22d3ee)",
                "from-purple-400 to-pink-400": "linear-gradient(to right, #a78bfa, #f472b6)",
                "from-rose-400 to-orange-400": "linear-gradient(to right, #fb7185, #fb923c)",
                "from-emerald-400 to-teal-400": "linear-gradient(to right, #34d399, #2dd4bf)"
              };
              
              const handleClick = () => {
                navigate(service.path);
              };

              const handleMouseEnter = () => {
                setHoveredIndex(index);
                setIsPaused(true);
              };

              const handleMouseLeave = () => {
                setHoveredIndex(null);
                setIsPaused(false);
              };
              
              return (
                <motion.div
                  key={service.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: shouldShowGlow ? 1 : 0.5,
                    scale: shouldShowGlow ? 1.15 : 0.95,
                    y: shouldShowGlow ? -5 : 0,
                    filter: shouldShowGlow ? "blur(0px)" : "blur(1px)",
                  }}
                  transition={{ 
                    type: "spring",
                    stiffness: 300,
                    damping: 25
                  }}
                  className="service-item"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Active/Hover Glow Effect Background */}
                  <AnimatePresence>
                    {shouldShowGlow && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1.1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        layoutId="activeGlow"
                        className="service-glow"
                        style={{ background: gradientMap[service.gradient] }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Pill Content */}
                  <div 
                    className={`service-pill ${shouldShowGlow ? "active" : ""}`}
                    style={{
                      boxShadow: shouldShowGlow ? `0 0 20px ${service.color}30` : 'none',
                      borderColor: shouldShowGlow ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.05)',
                      cursor: 'pointer'
                    }}
                    onClick={handleClick}
                  >
                    <div className="service-dot-container">
                      <motion.span 
                        animate={{ 
                          scale: shouldShowGlow ? [1, 1.5, 1] : 1,
                          opacity: shouldShowGlow ? [0.5, 0, 0.5] : 0.5
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity 
                        }}
                        className="service-dot-pulse"
                        style={{ backgroundColor: service.color }}
                      />
                      <span 
                        className="service-dot" 
                        style={{ backgroundColor: service.color }}
                      ></span>
                    </div>
                    
                    <span className={`service-label ${shouldShowGlow ? "active" : ""}`}>
                      {service.label}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <Button className="contact-btn" onClick={() => navigate("/contact")}>
          {t("hero.button")}
        </Button>
      </div>
    </section>
  );
}
